import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Button,
  Box,
  Center,
  useToast,
  useBreakpointValue,
  HStack,
  VStack,
  Text,
  Spinner,
  Link,
} from '@televet/kibble-ui';
import * as Sentry from '@sentry/react';
import { useHistory } from 'react-router-dom';
import { PanelViews } from 'shared/components/SidePanel/shared/types/enums/PanelViews';
import { useSidePanel } from 'shared/components/SidePanel/state/providers/SidePanelProvider';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import {
  InvoiceStatus as StripeInvoiceStatus,
  useStripeTerminalProvider,
} from 'shared/providers/StripeTerminalProvider';
import {
  CardFunding,
  Invoice,
  InvoiceLineItem,
  InvoiceLineItemType,
  InvoiceStatus,
  PaymentMedium,
  StripePaymentIntentStatus,
  useGetPaymentReceiptPdfLazyQuery,
  usePostCareInvoiceAccountCreditToPimsMutation,
} from 'shared/types/graphql';
import { formatPhoneNumber, getInvoiceIdentifierCopy, getMonetaryDisplayValue } from 'shared/utils';
import styled from 'styled-components/macro';
import { GET_SINGLE_INVOICE } from '../../../graphql/queries';
import UAParser from 'ua-parser-js';
import { Browsers } from 'shared/enums/Browsers';
import { formatDate, formatTime } from '../../../utils/formatDate';
import useClinicUser from 'shared/hooks/useClinicUser';
import usePersistedState from 'shared/hooks/usePersistedState';
import { defaultDiscoveredInvoiceDemoData, generateInvoiceLineItems } from 'pages/Settings/pages/DevToolsSettings';
import InvoiceLineItemTableList from './InvoiceLineItemTableList';
import { useMarkPetBenefitUsage } from 'shared/components/SidePanel/components/SearchPanel/components/Benefits/hooks/useMarkPetBenefitUsage';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { getCardBrandIcon } from '../../ChannelMessage/MessageBubble/utils';
import { convertDataToUrl, dataUrlToFile, downloadPdf } from '../../../utils/files';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { PersistedStateKeys } from 'shared/enums';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import FeeSaveAlert from 'shared/components/SidePanel/components/InvoicingPanel/components/FeeSaveAlert';
import { DoesStateRequireServiceFeesIntegratedIntoPricing } from '@televet/shared-utilities/flex-pay';
import { filterLineItems } from 'pages/Conversations/utils/invoice';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useAppDispatch } from 'state/hooks';
import { toggleInvoicePreviewModal } from 'pages/Conversations/state/conversationsSlice';

export type FilteredLineItemsType = Pick<
  InvoiceLineItem,
  | 'id'
  | 'amount'
  | 'description'
  | 'fee'
  | 'discount'
  | 'type'
  | 'clinicPets'
  | 'provider'
  | 'quantity'
  | 'service'
  | 'connectedCareBenefits'
>[];

interface IInvoiceProps {
  invoiceId: string;
  clinicPetParentId: string;
  defaultServiceFeePercentage: number;
  attachmentFeePercent?: number | undefined;
  invoiceAttachmentTotal?: string;
  updateInvoiceAttachmentDiscount?: (invoiceId: string, discount: number) => void;
  isReceipt?: boolean;
  totalPaid?: number;
  stripePaymentMethod?: string[] | null;
}

interface IInvoiceItemProps {
  isMobile: boolean;
}

const InvoiceComp = ({
  invoiceId,
  clinicPetParentId,
  defaultServiceFeePercentage,
  attachmentFeePercent,
  invoiceAttachmentTotal,
  isReceipt,
  totalPaid,
  stripePaymentMethod,
}: IInvoiceProps): ReactElement | null => {
  const toast = useToast();
  const [isDiscoveredInvoiceDemoModeEnabled] = usePersistedState(
    PersistedStateKeys.IsDiscoveredInvoiceDemoModeEnabled,
    false,
  );
  const [discoveredInvoiceDemoData] = usePersistedState(
    PersistedStateKeys.DiscoveredInvoiceDemoData,
    defaultDiscoveredInvoiceDemoData,
  );
  const { setRemoveInvoiceAttachment } = useSidePanelSearch();
  const [patientDiscounts, setPatientDiscounts] = useState<Record<string, number>>({});
  const [isPrintMode, setIsPrintMode] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { isMobile } = useResolutionProvider();
  const { setIsOpen, setCurrentView: setCurrentSidePanelView } = useSidePanel();
  const history = useHistory();
  const { currentClinicId } = useClinicUser();
  const {
    setCurrentPaymentMedium: setCurrentInvoicingPaymentMedium,
    setSelectedPetParent: setSelectedInvoicingPetParent,
    setCurrentInvoiceStatus,
    terminalOptions,
    currentInvoiceStatus,
    setInvoiceIds,
  } = useStripeTerminalProvider();
  const { getBrowser } = new UAParser();
  const browser: string = getBrowser().name || '';
  const { isFeatureEnabled } = useFeatureFlag();
  const { trackEvent } = useTracking();
  const dispatch = useAppDispatch();
  const { adjustments } = useMarkPetBenefitUsage(currentClinicId || '');

  const { data: invoiceData } = useQuery<{
    getInvoiceById: Invoice;
  }>(GET_SINGLE_INVOICE, {
    variables: {
      where: {
        id: invoiceId,
      },
      clinicPetParentId,
    },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    skip: !invoiceId,
  });

  const invoice = isDiscoveredInvoiceDemoModeEnabled
    ? discoveredInvoiceDemoData.invoices[0]
    : invoiceData?.getInvoiceById;
  const clinicPetParent = invoice?.clinicPetParent;
  const stripePaymentIntents = invoice?.stripePaymentIntents;
  const stripeReceipt = useMemo(
    () => stripePaymentIntents?.length && stripePaymentIntents[0].stripeReceipt,
    [stripePaymentIntents],
  );
  const clinic = clinicPetParent?.clinic;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav = navigator as any;
  const isSharingAvailable = useMemo(() => nav.share !== undefined && !!isMobile, [nav.share, isMobile]);

  // Safari and Firefox shows a blank page when the pdf is downloaded immediately, so we need to wait a bit before downloading the pdf
  const delayTime = browser === 'Safari' || browser === 'Firefox' ? 5000 : 0;

  const invoiceFilename = useMemo((): string => {
    const fileName = isReceipt ? 'Receipt' : 'Invoice';
    return (
      `${clinicPetParent?.lastName || ''}_
              ${formatDate(invoice?.date) || ''}_
              ${fileName}.pdf` || `$fileName.pdf`
    );
  }, [clinicPetParent, invoice, isReceipt]);

  const openPrintWindow = useCallback(
    async (source: string, fileName: string) => {
      const printWindow = document.createElement('iframe');
      printWindow.style.visibility = 'hidden';
      printWindow.id = 'printWindow';

      const src = await convertDataToUrl(source);
      printWindow.src = src;

      document.body.appendChild(printWindow);

      setTimeout(() => {
        if (src && printWindow.contentWindow) {
          printWindow.contentWindow.focus();
          printWindow.contentWindow.print();
        } else {
          downloadPdf(source, fileName);
        }
        setIsPrintMode(false);
        setIsDownloading(false);
      }, delayTime);
    },
    [delayTime],
  );

  const [getPaymentReceiptPdf, { loading: isPDFLoading }] = useGetPaymentReceiptPdfLazyQuery({
    variables: {
      data: {
        invoiceId,
      },
    },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    onCompleted: async (data) => {
      const { base64PDF } = data?.exportItemizedInvoiceAsReceiptPDF;
      const source = `data:application/pdf;base64,${base64PDF}`;
      const fileName = invoiceFilename;
      const file = await dataUrlToFile(source, fileName, 'application/pdf');

      if (isSharingAvailable && nav.canShare && nav.canShare({ files: [file] })) {
        nav
          .share({
            files: [file],
          })
          .then(() => {
            toast({
              title: 'Successfully shared receipt.',
              status: 'success',
              duration: 2000,
              position: 'top',
            });
            setIsDownloading(false);
          })
          .catch((e: Error) => {
            console.error('Error sharing receipt', e);
            openPrintWindow(source, fileName);
          });
        setIsPrintMode(false);
      } else {
        openPrintWindow(source, fileName);
      }
    },
    onError: (error) => {
      setIsDownloading(false);
      Sentry.captureException(error);
      toast({
        title: 'Error sharing receipt.',
        status: 'error',
        duration: 2000,
        position: 'top',
      });
    },
  });

  const handleInvoiceAction = (): void => {
    if (isDiscoveredInvoiceDemoModeEnabled) {
      toast({
        status: 'success',
        title: 'Invoice downloaded!',
        description: 'You can view the downloaded PDF to print it.',
      });
      return;
    }

    setIsDownloading(true);
    getPaymentReceiptPdf();
  };
  // accounts for any credits and taxes (but not service fees yet) added to the invoice
  const adjustedInvoiceTotal = useMemo((): number => {
    if (!invoiceAttachmentTotal && !invoice?.total && !stripePaymentIntents?.length) return 0;
    // if invoiceAttachmentTotal exists, we are in message draft, so don't look for payment intent
    if (stripePaymentIntents?.length && invoiceAttachmentTotal === undefined) {
      // return total associated with last payment intent
      return stripePaymentIntents[0].amount || 0;
    } else if (!!invoiceAttachmentTotal) {
      if (invoiceAttachmentTotal === '0' && isDiscoveredInvoiceDemoModeEnabled && invoice?.total) {
        return invoice.total - discoveredInvoiceDemoData.creditsApplied;
      }
      // return total from message composer invoice attachment
      return parseFloat(invoiceAttachmentTotal) * 100;
    }

    // lastly return original invoice total - includes tax, but not credits
    return invoice?.total || 0;
  }, [
    discoveredInvoiceDemoData.creditsApplied,
    invoice?.total,
    invoiceAttachmentTotal,
    isDiscoveredInvoiceDemoModeEnabled,
    stripePaymentIntents,
  ]);

  const serviceFeePercentage = useMemo(() => {
    // use value in message composer before message is sent
    if (attachmentFeePercent !== undefined) return attachmentFeePercent / 100;

    // use value from last associated payment intent
    if (stripePaymentIntents?.length && stripePaymentIntents[0].clientServiceFeePercent !== undefined) {
      return stripePaymentIntents[0].clientServiceFeePercent || 0;
    }

    // use clinic default percentage
    return defaultServiceFeePercentage / 100;
  }, [stripePaymentIntents, defaultServiceFeePercentage, attachmentFeePercent]);

  const serviceFee = useMemo(() => {
    // if attachmentFeePercent exists, we are in message draft, so don't look for payment intent
    if (stripePaymentIntents?.length && attachmentFeePercent === undefined) {
      if (stripePaymentIntents[0].clientServiceFee !== undefined) {
        // return fee amount associated with last payment intent created for this invoice
        return stripePaymentIntents[0].clientServiceFee || 0;
      } else if (adjustedInvoiceTotal && stripePaymentIntents[0].clientServiceFeePercent) {
        // calculate from fee percentage associated with last payment intent created for this invoice
        return adjustedInvoiceTotal * stripePaymentIntents[0].clientServiceFeePercent || 0;
      }
    }

    // calculate fee amount with clinic default service fee percentage if no payment intents exist
    return adjustedInvoiceTotal && serviceFeePercentage ? adjustedInvoiceTotal * serviceFeePercentage : 0;
  }, [adjustedInvoiceTotal, serviceFeePercentage, stripePaymentIntents, attachmentFeePercent]);

  const isSurchargeEnabled = useMemo(() => {
    return isFeatureEnabled(FeatureFlagName.SurchargeUpdates);
  }, [isFeatureEnabled]);

  // the below function does not actually factor in either fee so I am not going to add an extra query or prop to get the clinicFeePercentage
  // https://github.com/TeleVet/shared-utilities/blob/master/src/flex-pay/rules/StateRequiresServiceFeesIntegratedIntoPricing.ts
  const showServiceFeeAsIntegratedIntoPrice = useMemo(() => {
    return (
      DoesStateRequireServiceFeesIntegratedIntoPricing({
        state: clinic?.state || '',
        clinicServiceFeePercentage: 0,
        clientServiceFeePercentage: serviceFeePercentage,
      }) && isSurchargeEnabled
    );
  }, [clinic?.state, isSurchargeEnabled, serviceFeePercentage]);

  const serviceFeeIntegratedIntoPriceTotal = useMemo(() => {
    const surcharge = serviceFee > 0 ? serviceFee : (defaultServiceFeePercentage / 100) * adjustedInvoiceTotal;
    return showServiceFeeAsIntegratedIntoPrice ? adjustedInvoiceTotal + surcharge : adjustedInvoiceTotal;
  }, [adjustedInvoiceTotal, serviceFee, showServiceFeeAsIntegratedIntoPrice, defaultServiceFeePercentage]);

  const filteredLineItems: FilteredLineItemsType = useMemo(() => {
    if (isDiscoveredInvoiceDemoModeEnabled) return generateInvoiceLineItems(invoice?.total || 0);

    if (!invoice?.lineItems) return [];

    const lineItems = invoice?.lineItems.filter(
      (item) => item.type === InvoiceLineItemType.Code && item.amount + item.fee >= 0,
    );

    const invoiceLineItemTotal = lineItems.reduce((total, item) => (total += item.amount + item.fee), 0);

    const serviceFeeNotRounded = showServiceFeeAsIntegratedIntoPrice
      ? serviceFee > 0
        ? serviceFee
        : (defaultServiceFeePercentage / 100) * (adjustedInvoiceTotal || 0)
      : 0;

    const serviceFeeToBeAddedToLineItems = Math.round((serviceFeeNotRounded / 100) * 100);

    return filterLineItems({
      invoiceLineItemTotal,
      serviceFeeToBeAddedToLineItems,
      lineItems,
    });
  }, [
    invoice?.lineItems,
    showServiceFeeAsIntegratedIntoPrice,
    isDiscoveredInvoiceDemoModeEnabled,
    invoice?.total,
    serviceFee,
    defaultServiceFeePercentage,
    adjustedInvoiceTotal,
  ]);

  const filteredLineItemTotal = useMemo(() => {
    return filteredLineItems.reduce((total, item) => (total += item.amount + item.fee), 0);
  }, [filteredLineItems]);

  const invoicePets = useMemo(() => {
    return isDiscoveredInvoiceDemoModeEnabled
      ? [clinicPetParent?.pets?.[0]]
      : filteredLineItems?.map((item) => item.clinicPets).flat();
  }, [clinicPetParent?.pets, filteredLineItems, isDiscoveredInvoiceDemoModeEnabled]);

  // discounts can be added to the whole invoice, to an individual item, or (per Holly Springs) added as negative value line items
  const pimsInvoiceLevelDiscounts = useMemo(() => {
    let total = 0;

    invoice?.lineItems?.forEach((item) => {
      if (item.type !== InvoiceLineItemType.Payment && item.amount + item.fee < 0) {
        total -= item.amount + item.fee + item.discount;
      }
    });

    return total;
  }, [invoice?.lineItems]);

  const lineItemLevelDiscounts = useMemo(() => {
    let total = 0;
    invoice?.lineItems?.forEach((item) => {
      if (item.type === InvoiceLineItemType.Code && item.amount + item.fee >= 0) {
        total += item.discount;
      }
    });
    return total;
  }, [invoice?.lineItems]);

  const totalPimsDiscounts = useMemo(() => {
    const total = invoice?.lineItems?.reduce((sum, item) => sum + item.discount, 0) || 0;
    return total;
  }, [invoice?.lineItems]);

  const careDiscounts = useMemo(() => {
    let total = 0;
    filteredLineItems?.forEach((item) => {
      item.connectedCareBenefits?.forEach((benefit) => {
        benefit.adjustedAmount && (total += benefit.adjustedAmount);
      });
    });
    return total;
  }, [filteredLineItems]);

  const subtotal = useMemo(() => {
    const surcharge = showServiceFeeAsIntegratedIntoPrice
      ? serviceFee > 0
        ? serviceFee
        : (defaultServiceFeePercentage / 100) * (adjustedInvoiceTotal || 0)
      : 0;

    return (
      (invoice?.subtotal || 0) -
      careDiscounts +
      pimsInvoiceLevelDiscounts +
      totalPimsDiscounts -
      lineItemLevelDiscounts +
      surcharge
    );
  }, [
    invoice,
    careDiscounts,
    pimsInvoiceLevelDiscounts,
    showServiceFeeAsIntegratedIntoPrice,
    serviceFee,
    defaultServiceFeePercentage,
    lineItemLevelDiscounts,
    totalPimsDiscounts,
    adjustedInvoiceTotal,
  ]);

  const creditsApplied = useMemo((): number => {
    if (isDiscoveredInvoiceDemoModeEnabled) return discoveredInvoiceDemoData.creditsApplied;

    if (!invoice?.total || !adjustedInvoiceTotal) return 0;

    // return difference between original invoice total and adjustedInvoiceTotal. Care discounts are already included in adjusted total, so remove from original total too
    return invoice.total - careDiscounts - adjustedInvoiceTotal;
  }, [
    isDiscoveredInvoiceDemoModeEnabled,
    discoveredInvoiceDemoData.creditsApplied,
    invoice?.total,
    adjustedInvoiceTotal,
    careDiscounts,
  ]);
  // validate sum of line items with taxes and pims discounts matches invoice total
  useEffect(() => {
    if (!invoice || invoice.status === InvoiceStatus.Closed) return;

    const invoiceTotal = filteredLineItemTotal + invoice.taxes - totalPimsDiscounts;

    if (invoiceTotal !== invoice.total) {
      const error = new Error(
        `Invoice ${invoiceId} components (${invoiceTotal}) do not add up to invoice total (${invoice.total})!`,
      );
      Sentry.captureException(error);
    }
  }, [filteredLineItemTotal, invoice, totalPimsDiscounts, serviceFee, invoiceId]);

  const [postCareInvoiceAccountCreditToPims, { loading: isPostingCreditToPims }] =
    usePostCareInvoiceAccountCreditToPimsMutation({
      variables: {
        data: {
          invoiceId: invoiceId,
          paymentAmount: careDiscounts,
        },
      },
    });

  const isFullyDiscounted = useMemo(() => {
    return adjustedInvoiceTotal + serviceFee < 50;
  }, [adjustedInvoiceTotal, serviceFee]);

  const sendToSidePanel = async (): Promise<void> => {
    if (isFullyDiscounted) {
      try {
        const res = await postCareInvoiceAccountCreditToPims();
        const { data } = res;
        const success = data?.postCareInvoiceAccountCreditToPims?.success;
        const error = data?.postCareInvoiceAccountCreditToPims?.error;
        if (success) {
          toast({
            title: 'Invoice sent to PIMS',
            status: 'success',
          });
          setRemoveInvoiceAttachment(true);
        } else {
          toast({
            title: `Error sending invoice to PIMS: ${error}`,
            status: 'error',
          });
        }
      } catch (e) {
        Sentry.captureException(e);
        toast({
          title: 'There was an error adding the credit to the PIMS account',
          status: 'error',
        });
      }

      dispatch(toggleInvoicePreviewModal({ isOpen: false }));
      return;
    }

    dispatch(toggleInvoicePreviewModal({ isOpen: false }));

    if (currentInvoiceStatus !== StripeInvoiceStatus.Pending) {
      setIsOpen(false);
      setTimeout(() => {
        setCurrentSidePanelView(PanelViews.Invoicing);
        setIsOpen(true);
        if (terminalOptions && terminalOptions.length > 0) {
          setCurrentInvoicingPaymentMedium(PaymentMedium.StripeTerminal);
        } else {
          setCurrentInvoicingPaymentMedium(PaymentMedium.StripeVirtualTerminal);
        }
        setCurrentInvoiceStatus(StripeInvoiceStatus.Default);
        setSelectedInvoicingPetParent(clinicPetParent);
        setInvoiceIds(invoice?.id ? [invoice.id] : []);
      }, 0);
    }
  };

  const invoiceRef = useRef(null);

  // invoice is open and without any successful payment intents
  const invoicePayable = useMemo(() => {
    return (
      invoice?.status === InvoiceStatus.Open &&
      !stripePaymentIntents?.some((intent) => intent.status === StripePaymentIntentStatus.Succeeded)
    );
  }, [invoice?.status, stripePaymentIntents]);

  const invoiceActionButton =
    isReceipt && isSharingAvailable && isMobile ? (
      <Button
        variant="secondary"
        iconName="send"
        onClick={handleInvoiceAction}
        isLoading={isPDFLoading || isDownloading}
      >
        Share Receipt
      </Button>
    ) : (
      <Button
        variant="secondary"
        iconName="form"
        size="sm"
        onClick={handleInvoiceAction}
        isLoading={isPDFLoading || isDownloading}
      >
        Print or Download PDF
      </Button>
    );

  const mobileButton = useBreakpointValue({ base: 0, md: 6 });

  const isSyncable = useMemo(() => {
    return (
      careDiscounts > 0 &&
      isFullyDiscounted &&
      isFeatureEnabled(FeatureFlagName.BitwerxPaymentWritebacks) &&
      isFeatureEnabled(FeatureFlagName.CarePlans)
    );
  }, [careDiscounts, isFeatureEnabled, isFullyDiscounted]);

  const showSaveFeeAlert = useMemo(() => {
    return isFeatureEnabled(FeatureFlagName.SurchargeUpdates) && serviceFee > 0 && !isReceipt && !isFullyDiscounted;
  }, [isFeatureEnabled, serviceFee, isFullyDiscounted, isReceipt]);

  const showCashDiscount = useMemo(() => {
    return (
      (isReceipt ?? false) &&
      isFeatureEnabled(FeatureFlagName.SurchargeUpdates) &&
      (!stripePaymentMethod?.includes(CardFunding.Credit) ?? false)
    );
  }, [isReceipt, isFeatureEnabled, stripePaymentMethod]);

  const paymentOrSyncButton = !isReceipt && invoicePayable && (
    <Button
      onClick={(): void => {
        const eventName = isFullyDiscounted
          ? ConversationEvents.InvoiceSyncBenegitUsageToPims
          : ConversationEvents.InvoicePayNow;
        trackEvent(eventName);
        sendToSidePanel();
        if (isMobile) {
          history.push(`/conversations`);
        }
      }}
      hidden={isFullyDiscounted && !isSyncable}
      disabled={isPostingCreditToPims}
      mt={mobileButton}
    >
      {isSyncable ? 'Sync Benefit Usage to Pims' : 'Pay Invoice Now'}
    </Button>
  );

  const { isClientIdSupported, isPatientIdSupported } = useIntegrationsProvider();

  if (invoice) {
    return (
      <InvoiceContainer ref={invoiceRef} browser={browser}>
        <InvoiceHeader>
          {isReceipt && 'Receipt for'} Invoice {getInvoiceIdentifierCopy(invoice.identifier, invoice.createdAt)}
        </InvoiceHeader>
        <FullDivider />
        <InvoiceBody>
          <InvoiceContactInfo>
            <DetailContainer>
              <ClinicLogo src={clinic?.logo || ''} />
              <InvoiceDetails>
                {invoice.date && (
                  <div>
                    <DetailHeader>Invoice Created: </DetailHeader>
                    {`${formatDate(invoice.date)}, ${formatTime(invoice.date)}`}
                  </div>
                )}
                <div>
                  <DetailHeader>Animal(s) Seen: </DetailHeader>
                  {invoicePets?.length
                    ? Array.from(new Set(invoicePets?.map((pet) => pet?.name))).join(', ')
                    : 'Not Specified'}
                </div>
              </InvoiceDetails>
            </DetailContainer>
            <ContactInfoContainer isMobile={isMobile}>
              <ContactInfo>
                <ContactBlockHeader>{clinic?.name}</ContactBlockHeader>
                <ContactBlock>
                  <div>{clinic?.addressLine1}</div>
                  <div>{clinic?.addressLine2}</div>
                  <div>
                    {clinic?.city}
                    {', '}
                    {clinic?.state}
                    {', '}
                    {clinic?.postalCode}
                  </div>
                </ContactBlock>
                <ContactBlock>
                  <div>{clinic?.clinicEmail}</div>
                  <div>{formatPhoneNumber(clinic?.phone)}</div>
                  {clinic?.websiteUrl && (
                    <ClinicUrl data-hide-on-download="true" target="_blank" rel="noreferrer" href={clinic.websiteUrl}>
                      Visit Website
                    </ClinicUrl>
                  )}
                </ContactBlock>
              </ContactInfo>
              <ContactInfo>
                <ContactBlockHeader>Bill to:</ContactBlockHeader>
                <ContactBlock>
                  <CustomerName>
                    {clinicPetParent?.lastName}, {clinicPetParent?.firstName}
                  </CustomerName>
                </ContactBlock>
                <ContactBlock>
                  <div>{clinicPetParent?.email}</div>
                  <div>{formatPhoneNumber(clinicPetParent?.phoneNumber)}</div>
                </ContactBlock>
              </ContactInfo>
            </ContactInfoContainer>
          </InvoiceContactInfo>
          <InvoiceLineItemTableList
            isMobile={isMobile}
            filteredLineItems={filteredLineItems}
            patientDiscounts={patientDiscounts}
            setPatientDiscounts={setPatientDiscounts}
            adjustments={adjustments}
            fromInvoicePreview={true}
            isPrintMode={isPrintMode}
            isReceipt={isReceipt}
          />
          <HStack alignItems="flexStart" justifyContent="space-between">
            {!isMobile && (
              <VStack justifyContent="space-between" alignItems="flex-start">
                {stripePaymentMethod && isReceipt && (
                  <Box display="block" alignItems="flex-start" pt="2">
                    <Text size="sm" fontWeight="bold" variant="subtle">
                      Payment Method
                    </Text>
                    <HStack alignItems="center" p="1">
                      <b>{getCardBrandIcon(`${stripePaymentMethod ? stripePaymentMethod[0] : ''}`, '20px', '29px')}</b>
                      <Text size="sm" pb="1">
                        -{stripePaymentMethod ? stripePaymentMethod[1] : ''}
                      </Text>
                    </HStack>
                    {!!stripeReceipt && (
                      <>
                        <Text size="sm" variant="subtle">
                          Application Name: {stripeReceipt.applicationName}
                        </Text>
                        <Text size="sm" variant="subtle">
                          AID: {stripeReceipt.dedicatedFileName}, Account Type: {stripeReceipt.accountType}
                        </Text>
                      </>
                    )}
                  </Box>
                )}
              </VStack>
            )}
            <InvoiceAmountContainer>
              <AmountLineItem>
                <b>Subtotal:</b>
                <b>{getMonetaryDisplayValue(subtotal, true)}</b>
              </AmountLineItem>
              <PartialDivider />
              <AmountLineItem>
                <div>Discounts:</div>
                {isReceipt ? (
                  <div>
                    -
                    {getMonetaryDisplayValue(
                      pimsInvoiceLevelDiscounts + totalPimsDiscounts - lineItemLevelDiscounts,
                      true,
                    )}
                  </div>
                ) : (
                  <RedText>
                    -(
                    {getMonetaryDisplayValue(
                      pimsInvoiceLevelDiscounts + totalPimsDiscounts - lineItemLevelDiscounts,
                      true,
                    )}
                    )
                  </RedText>
                )}
              </AmountLineItem>
              <AmountLineItem>
                <div>Taxes:</div>
                <div>{invoice.taxes !== undefined ? getMonetaryDisplayValue(invoice.taxes, true) : '$0.00'}</div>
              </AmountLineItem>
              <PartialDivider />
              <AmountLineItem font-weight="bold">
                <b>Invoice Total:</b>
                <b>{getMonetaryDisplayValue(serviceFeeIntegratedIntoPriceTotal + creditsApplied, true)}</b>
              </AmountLineItem>
              <PartialDivider />
              <AmountLineItem>
                <div>Previous Balance:</div>
                {creditsApplied > 0 ? (
                  <RedText>-({getMonetaryDisplayValue(creditsApplied, true)})</RedText>
                ) : (
                  <div>{getMonetaryDisplayValue(-1 * creditsApplied, true)}</div>
                )}
              </AmountLineItem>
              {!showServiceFeeAsIntegratedIntoPrice && (
                <AmountLineItem>
                  <div>
                    Service Fee (
                    {(showCashDiscount && serviceFee > 0 ? serviceFeePercentage : defaultServiceFeePercentage).toFixed(
                      2,
                    )}
                    %):
                  </div>
                  <div>
                    $
                    {getMonetaryDisplayValue(
                      showCashDiscount && serviceFee > 0
                        ? serviceFee
                        : (defaultServiceFeePercentage * adjustedInvoiceTotal) / 100 || 0,
                    )}
                  </div>
                </AmountLineItem>
              )}

              {showCashDiscount && (
                <AmountLineItem>
                  <Text p={0} m={0} size="sm" color="text.success">
                    Cash discount
                  </Text>
                  <Text p={0} m={0} size="sm" color="text.success">
                    -{getMonetaryDisplayValue((defaultServiceFeePercentage * adjustedInvoiceTotal) / 100, true)}
                  </Text>
                </AmountLineItem>
              )}
              <PartialDivider />
              <AmountLineItem>
                <TotalAmount>{isReceipt ? 'Total Paid' : 'Balance Due:'}</TotalAmount>
                <TotalAmount>
                  {isFullyDiscounted && !isReceipt
                    ? '$0.00'
                    : getMonetaryDisplayValue(isReceipt ? totalPaid : adjustedInvoiceTotal + serviceFee, true) ||
                      '$0.00'}
                </TotalAmount>
              </AmountLineItem>

              <FeeSaveAlert
                serviceFee={serviceFee / 100}
                showSaveAlert={showSaveFeeAlert}
                styleProps={{
                  marginTop: '10px',
                }}
              />
              {!isMobile && !isPrintMode && (
                <>
                  <div>{paymentOrSyncButton || null}</div>
                  <Box as="button" disabled={isPDFLoading} mt={3}>
                    {invoiceActionButton}
                  </Box>
                </>
              )}
            </InvoiceAmountContainer>
          </HStack>
        </InvoiceBody>
        <InvoiceFooter>
          {isMobile && !isPrintMode && (
            <>
              <FullDivider data-hide-on-download="true" />
              <MobileButtonContainer data-hide-on-download="true">
                {paymentOrSyncButton || null}
                <Box mt={3}>{isPrintMode ? null : invoiceActionButton}</Box>
              </MobileButtonContainer>
            </>
          )}
          {isClientIdSupported && isPatientIdSupported && !isReceipt && (
            <FooterDetails>
              <DetailItem isMobile={isMobile}>
                <DetailHeader>Customer ID:</DetailHeader>
                {clinicPetParent?.pimsId || 'Not Specified'}
              </DetailItem>
              <DetailItem isMobile={isMobile}>
                <DetailHeader>Patient IDs:</DetailHeader>
                {invoicePets?.length
                  ? Array.from(new Set(invoicePets?.map((pet) => pet?.pimsId || `Unknown ID: ${pet?.name}`))).join(', ')
                  : 'Not Specified'}
              </DetailItem>
              <DetailItem isMobile={isMobile}>
                <DetailHeader>Invoice ID:</DetailHeader>
                {invoice.pimsInvoice?.pimsId || 'Not Specified'}
              </DetailItem>
              <DetailItem isMobile={isMobile}>
                <DetailHeader>Clinic ID:</DetailHeader>
                {clinic?.customerId || 'Not Specified'}
              </DetailItem>
            </FooterDetails>
          )}
          <PoweredByOtto>
            Powered by:<span> </span>
            <Link size="sm" target="_blank" rel="noreferrer" href="https://www.otto.vet">
              www.otto.vet
            </Link>
          </PoweredByOtto>
        </InvoiceFooter>
      </InvoiceContainer>
    );
  }
  return (
    <LoadingContainer>
      <Center>
        <h1>Loading {isReceipt ? 'Receipt' : 'Invoice'}</h1>
      </Center>
      <Center>
        <Spinner />
      </Center>
    </LoadingContainer>
  );
};

export default InvoiceComp;

const InvoiceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 18px;
  font-weight: 600;
  line-height: 22px;
  text-align: left;
  padding: 18px 30px;
`;

const InvoiceBody = styled.div`
  padding: 25px 30px;
  flex-grow: 1;
`;

const InvoiceFooter = styled.div``;

const DetailContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
`;

const ContactInfoContainer = styled.div<IInvoiceItemProps>`
  flex: 1 1 auto;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  flex-direction: ${({ isMobile }: IInvoiceItemProps): string => (isMobile ? 'column' : 'row')};

  @media print {
    flex-direction: row;
  }
`;

const FullDivider = styled.div`
  height: 1px;
  border: 1px solid #e8e9e9;
`;

const PartialDivider = styled.div`
  width: 100%;
  height: 1px;
  border: 1px solid #e8e9e9;
  margin: auto;
`;

const InvoiceContactInfo = styled.div`
  padding-bottom: 25px;
`;

const ClinicLogo = styled.img`
  max-height: 60px;
  object-fit: contain;
`;

const InvoiceDetails = styled.div`
  font-size: 12px;
  text-align: right;
`;

const DetailHeader = styled.span`
  font-weight: 600;
`;

const ContactInfo = styled.div`
  padding-top: 15px;
  word-break: break-word;
`;

const ContactBlockHeader = styled.div`
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
`;

const ContactBlock = styled.div`
  font-size: 14px;
  line-height: 18px;

  padding-top: 10px;
`;

const ClinicUrl = styled.a`
  color: var(--chakra-colors-text-interactive);
  :hover {
    text-decoration: underline;
  }
`;

const CustomerName = styled.div`
  font-size: 16px;
  line-height: 20px;
`;

const InvoiceLineItemTableHead = styled.thead`
  border-bottom: 2px solid #e8e9e9;
`;

const InvoiceLineItemTableRow = styled.tr`
  height: 50px;
  &:nth-of-type(2n) {
    background: #f3f9fa;
  }

  td:last-child,
  th:last-child {
    text-align: right;
    padding-right: 20px;
  }
`;

const InvoiceAmountContainer = styled.div`
  display: flex;
  width: 200px;
  flex-direction: column;
  align-items: flex-end;
  align-content: space-between;
  padding-top: 15px;
  margin-left: auto;
`;

const AmountLineItem = styled.div`
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;

  padding: 3px 0;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const RedText = styled.div`
  color: #ff0000;
`;

const TotalAmount = styled.div`
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;

  padding-top: 7px;
`;

const MobileButtonContainer = styled.div`
  padding: 20px;
`;

const FooterDetails = styled.div`
  background: #f8f9fb;
  border-top: 1px solid #e8e9e9;
  border-bottom: 1px solid #e8e9e9;
  display: flex;
  padding: 10px;

  div + div {
    border-left: 1px dashed #e0e0e0;
  }
`;

const DetailItem = styled.div<IInvoiceItemProps>`
  font-size: 12px;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  word-break: break-word;
  width: 25%;
  padding-left: ${({ isMobile }: IInvoiceItemProps): string => (isMobile ? '5px' : '20px')};
`;

const PoweredByOtto = styled.div`
  height: 40px;
  font-size: 14px;
  line-height: 19px;

  display: flex;
  justify-content: center;
  align-items: center;

  a {
    color: #52b0c3;
    padding-left: 4px;

    :hover {
      text-decoration: underline;
    }
  }
`;

export const LoadingContainer = styled.div`
  padding: 120px 20px;

  h1 {
    padding: 20px 0;
    font-size: 22px;
    font-weight: bold;
  }
`;

const InvoiceContainer = styled.div<{ browser: string }>`
  border: 1px solid var(--chakra-colors-border-default);
  background: var(--chakra-colors-background-default);
  display: flex;
  flex-direction: column;

  @media print {
    min-height: ${({ browser }): string => {
      // Safari has forced print margins that can't be turned off,
      // so reduce min height to fit on one page by default
      switch (browser) {
        case Browsers.MobileSafari:
          return '8.75in';
        case Browsers.Safari:
          return '9.5in';
        default:
          return '11in';
      }
    }};

    border: none;

    *:not(table) {
      page-break-inside: avoid;
    }

    *[data-hide-on-download='true'] {
      visibility: hidden;
    }

    ${InvoiceAmountContainer} {
      page-break-before: auto;
    }

    ${InvoiceLineItemTableRow} {
      page-break-before: auto;
    }

    ${InvoiceLineItemTableHead} {
      display: table-row-group;
    }
  }
`;
